
import { useState, useMemo } from 'react';
import { useBoardContext } from '@/contexts/BoardContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import Header from '@/components/Header';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { CalendarCardDialog } from '@/components/CalendarCardDialog';
import { CardPresenceProvider } from '@/contexts/OptimizedCardPresenceContext';
import type { Tables } from '@/integrations/supabase/types';

type CardType = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

const Calendar = () => {
  const { cards, profiles, tagDefinitions, getTagName, getTagColor, currentBoardId } = useBoardContext();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCard, setSelectedCard] = useState<CardType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const cardsWithDates = useMemo(() => {
    return cards?.filter(card => card.start_date || card.end_date) || [];
  }, [cards]);

  const getCardsForDate = (date: number) => {
    const targetDate = new Date(currentYear, currentMonth, date);
    const targetDateStr = targetDate.toISOString().split('T')[0];

    return cardsWithDates.filter(card => {
      const startDate = card.start_date;
      const endDate = card.end_date;

      if (startDate && endDate) {
        return targetDateStr >= startDate && targetDateStr <= endDate;
      } else if (startDate) {
        return targetDateStr === startDate;
      } else if (endDate) {
        return targetDateStr === endDate;
      }
      return false;
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleCardClick = (card: CardType) => {
    // Add assigned profile data to the card
    const cardWithProfile = {
      ...card,
      assigned_profile: card.assigned_to ? profiles?.find(p => p.id === card.assigned_to) : undefined
    };
    setSelectedCard(cardWithProfile);
    setDialogOpen(true);
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className="min-h-32 border border-border"></div>
      );
    }

    // Days of the month
    for (let date = 1; date <= daysInMonth; date++) {
      const cardsForDate = getCardsForDate(date);
      const isToday = new Date().toDateString() === new Date(currentYear, currentMonth, date).toDateString();

      days.push(
        <div
          key={date}
          className={`min-h-32 border border-border p-2 ${
            isToday ? 'bg-blue-50 dark:bg-blue-950 border-blue-300 dark:border-blue-700' : 'hover:bg-muted'
          }`}
        >
          <div className={`font-medium mb-2 ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'}`}>
            {date}
          </div>
          <div className="space-y-1">
            {cardsForDate.slice(0, 3).map((card) => {
              const assignedProfile = profiles?.find(p => p.id === card.assigned_to);
              return (
                <div
                  key={card.id}
                  className="text-xs p-1 bg-card rounded border border-border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleCardClick(card)}
                >
                  <div className="font-medium text-card-foreground truncate">{card.title}</div>
                  {assignedProfile && (
                    <div className="text-muted-foreground truncate">
                      {assignedProfile.full_name || assignedProfile.username}
                    </div>
                  )}
                  {card.tags && card.tags.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {card.tags.slice(0, 2).map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs px-1 py-0" style={{ backgroundColor:getTagColor(tag), border:"none", color:"white" }}>
                          {getTagName(tag)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {cardsForDate.length > 3 && (
              <div className="text-xs text-muted-foreground p-1">
                +{cardsForDate.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // If no board is selected, show a message
  if (!currentBoardId) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
          <Header />
          <div className="container mx-auto p-6">
            <Card className="bg-card shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="text-muted-foreground">
                  Please select a board to view the calendar.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <CardPresenceProvider boardId={currentBoardId}>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
          <Header />
          
          <div className="container mx-auto p-6">
            <Card className="bg-card shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Task Calendar
                  </CardTitle>
                  
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={goToToday}>
                      Today
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateMonth('prev')}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      <h2 className="text-lg font-semibold min-w-48 text-center">
                        {monthNames[currentMonth]} {currentYear}
                      </h2>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateMonth('next')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Week day headers */}
                <div className="grid grid-cols-7 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="p-2 text-center font-medium text-muted-foreground border-b border-border">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7">
                  {renderCalendarDays()}
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card className="mt-6 bg-card shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm">Legend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-50 dark:bg-blue-950 border border-blue-300 dark:border-blue-700 rounded"></div>
                    Today
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-card border border-border rounded"></div>
                    Tasks scheduled (click to view details)
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Card Detail Dialog */}
          {selectedCard && (
            <CalendarCardDialog
              card={selectedCard}
              open={dialogOpen}
              onOpenChange={setDialogOpen}
            />
          )}
        </div>
      </CardPresenceProvider>
    </ThemeProvider>
  );
};

export default Calendar;
